import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { siteInfo, testimonials } from '@/mocks/anime';
import { useState, useCallback } from 'react';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const submitAddr = form.getAttribute('data-submit-addr') || '';
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setFormError('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后再试');
        } else {
          setFormStatus('error');
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请检查信息后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络异常，请检查网络连接后重试');
    }
  }, []);

  const aboutJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
      description: siteInfo.description,
      email: siteInfo.email,
      telephone: siteInfo.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: siteInfo.address,
        addressCountry: 'CN',
      },
      foundingDate: siteInfo.established,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `关于我们 - ${siteInfo.name}`,
      description: `了解${siteInfo.name}的品牌故事、发展历程和联系方式`,
      url: `${siteInfo.url}/about`,
      isPartOf: {
        '@type': 'WebSite',
        name: siteInfo.name,
        url: siteInfo.url,
      },
    },
  ];

  return (
    <>
      <SEOHead
        title="关于我们"
        description={`${siteInfo.name}是${siteInfo.tagline}，成立于${siteInfo.established}年。了解我们的品牌故事、用户口碑与联系方式，共同打造优质的手机动漫平台。`}
        keywords="樱花动漫,关于我们,手机动漫门户,动漫大全,联系方式"
        canonicalUrl={`${siteInfo.url}/about`}
        jsonLd={aboutJsonLd}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          {/* Hero */}
          <section className="py-16 md:py-24 bg-background-100">
            <div className="w-full max-w-3xl mx-auto px-4 md:px-6 text-center">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                关于{siteInfo.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {siteInfo.name}成立于{siteInfo.established}年，是{siteInfo.tagline}
                ，致力于为广大动漫爱好者提供优质的动漫大全浏览体验。
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="py-14 md:py-20">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">
                品牌故事
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-3">我们的初心</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    在移动互联网高速发展的时代，我们注意到越来越多的动漫爱好者倾向于使用手机观看动漫。然而，许多动漫网站并未针对移动端进行优化，导致浏览体验不佳。{siteInfo.name}应运而生，我们从一开始就坚持移动端优先的设计理念，为动漫迷们打造一个真正适合手机浏览的动漫门户网站。
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-3">我们的愿景</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们希望通过{siteInfo.name}，让每一位动漫爱好者都能随时随地享受高质量的动漫内容。无论你是在通勤路上、午休时间还是睡前片刻，打开手机就能进入精彩的动漫世界。我们将持续优化用户体验，丰富动漫大全资源，成为大家最信赖的手机动漫门户网站。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <p className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-1">8+</p>
                  <p className="text-xs text-foreground-500">年行业经验</p>
                </div>
                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <p className="text-3xl md:text-4xl font-heading font-bold text-accent-500 mb-1">5000+</p>
                  <p className="text-xs text-foreground-500">动漫资源</p>
                </div>
                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <p className="text-3xl md:text-4xl font-heading font-bold text-secondary-500 mb-1">300万+</p>
                  <p className="text-xs text-foreground-500">月活跃用户</p>
                </div>
                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <p className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-1">98%</p>
                  <p className="text-xs text-foreground-500">用户满意度</p>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-14 md:py-20">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((t) => (
                  <div
                    key={t.id}
                    className="bg-background-50 rounded-xl p-5 border border-background-200/70"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                        <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                        <div className="flex items-center gap-0.5 mt-0.5">
                          {Array.from({ length: t.rating }).map((_, i) => (
                            <span key={i} className="w-3 h-3 flex items-center justify-center text-accent-500">
                              <i className="ri-star-fill text-[10px]"></i>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed">{t.content}</p>
                    <p className="text-[10px] text-foreground-400 mt-3">{t.date}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-14 md:py-20 bg-background-100" id="contact">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3 text-center">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 text-center mb-10">
                有任何问题或建议，欢迎与我们联系
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {/* Contact Info */}
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">电子邮箱</p>
                      <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500 flex-shrink-0">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">联系电话</p>
                      <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-500 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">公司地址</p>
                      <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div>
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    data-submit-addr="https://readdy.ai/api/form/d9orqhqqs38sj6jc7ed0"
                    className="bg-background-50 rounded-xl p-6 border border-background-200/70 space-y-4"
                  >
                    <div>
                      <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="about-name"
                        name="name"
                        required
                        className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 bg-background-50 text-foreground-950"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="about-email"
                        name="email"
                        required
                        className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 bg-background-50 text-foreground-950"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1">
                        留言内容
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 bg-background-50 text-foreground-950 resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      />
                    </div>
                    {/* Honeypot */}
                    <input
                      type="text"
                      name="phone_alt"
                      className="form-hp-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <button
                      type="submit"
                      className="w-full px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      提交留言
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-xs text-secondary-500 text-center">感谢您的留言，我们会尽快回复！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-xs text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}